import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './learn.css';

// =========================================================================
// /learn — Index of all Learn articles. Groups by pillar.
// Data: /learn/articles.json
// =========================================================================

const PILLAR_LABELS = {
  loyalty: 'Loyalty',
  campaigns: 'Campaigns',
  marketplace: 'Marketplace intelligence',
  mbo: 'MBO',
  sis: 'SIS',
  ebo: 'EBO',
  'social-commerce': 'Social commerce',
  malls: 'Malls',
  'retail-ai': 'Governed retail AI',
};

const LearnIndex = () => {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    document.title = 'Fundle Learn — Retail & mall AI handbook';
    const setM = (n, v) => {
      let el = document.head.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', n); document.head.appendChild(el); }
      el.setAttribute('content', v);
    };
    setM('description', 'Fundle Learn — a growing handbook of practical articles on retail and mall AI: loyalty, campaigns, marketplaces, MBO, SIS, EBO, social commerce and governed AI workflows.');
  }, []);

  // Source the index from BOTH the shipped static seed AND every published
  // content-engine article (scheduler- or CMS-authored). DB articles are served
  // fully dynamically — not baked into the seed — and merged here (dedup by slug,
  // seed preferred) so newly published /learn/<slug> pages are discoverable and
  // internally linked rather than orphaned.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    Promise.all([
      fetch('/learn/articles.json').then((r) => r.json()).then((j) => j.articles || []).catch(() => []),
      fetch(`${base}/api/content/v1/public/articles`).then((r) => (r.ok ? r.json() : { items: [] })).then((j) => j.items || []).catch(() => []),
    ]).then(([seed, cms]) => {
      const bySlug = new Map();
      seed.forEach((a) => a && a.slug && bySlug.set(a.slug, a));
      cms.forEach((a) => { if (a && a.slug && !bySlug.has(a.slug)) bySlug.set(a.slug, a); });
      setArticles(Array.from(bySlug.values()));
    });
  }, []);

  const grouped = (articles || []).reduce((acc, a) => {
    (acc[a.pillar] = acc[a.pillar] || []).push(a);
    return acc;
  }, {});

  return (
    <main data-testid="learn-index">
      <section style={{ padding: '64px 24px 32px', background: '#0D1B2A', color: '#F8FAFC' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: '#C6F432', marginBottom: 14 }}>
            Fundle Learn
          </p>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: 1.05, margin: 0, fontWeight: 800 }}>
            A practical handbook<br />for retail AI.
          </h1>
          <p style={{ fontSize: 17, marginTop: 20, maxWidth: 720, color: '#CBD5E1' }}>
            Short, honest articles for retail and mall teams exploring loyalty, campaigns,
            marketplaces, MBO/SIS/EBO, social commerce and governed Fundle Brain workflows.
          </p>
          <p style={{ marginTop: 16, fontSize: 14 }}>
            <Link to="/answers" style={{ color: '#C6F432', fontWeight: 700 }}>Browse the full answer library →</Link>
          </p>
        </div>
      </section>

      <section style={{ padding: '48px 24px 96px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {articles === null && <p>Loading…</p>}
          {articles && Object.keys(grouped).map((pil) => (
            <section key={pil} data-testid={`learn-pillar-${pil}`} style={{ marginBottom: 44 }}>
              <h2 style={{ fontSize: 22, color: '#0D1B2A', borderBottom: '1px solid #E2E8F0', paddingBottom: 10 }}>
                {PILLAR_LABELS[pil] || pil} <span style={{ color: '#4b5a74', fontWeight: 400, fontSize: 14 }}>({grouped[pil].length})</span>
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 16 }}>
                {grouped[pil].map((a) => (
                  <li key={a.slug}>
                    <Link
                      to={`/learn/${a.slug}`}
                      data-testid={`learn-card-${a.slug}`}
                      style={{
                        display: 'block',
                        padding: 20,
                        border: '1px solid #E2E8F0',
                        borderRadius: 12,
                        background: '#F8FBFF',
                        textDecoration: 'none',
                        color: '#0D1B2A',
                      }}
                    >
                      <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: '#1E3A8A', margin: 0 }}>
                        {PILLAR_LABELS[pil] || pil}
                      </p>
                      <p style={{ fontSize: 17, fontWeight: 700, margin: '10px 0 8px', lineHeight: 1.3 }}>
                        {a.title}
                      </p>
                      <p style={{ fontSize: 13.5, color: '#4b5a74', margin: 0, lineHeight: 1.5 }}>
                        {a.meta_description}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </section>
    </main>
  );
};

export default LearnIndex;
